import { Button, EmptyState, Table, type TableColumn } from '@bennows/boows-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../lib/format';
import { useAuditActionLabel } from '../audit/useAuditActionLabel';
import { useTenantAudit, type AuditEvent } from './useTenant';

const CARD_BREAKPOINT = 640;

/** What operators did with this tenant, newest first (REQ-140-005). */
export function TenantAuditTab({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation();
  const audit = useTenantAudit(tenantId);
  const rows = audit.data?.pages.flatMap((page) => page.data) ?? [];
  const action = useAuditActionLabel();
  const who = (row: AuditEvent) => row.operator?.display_name ?? t('tenant.audit.system');
  const columns: TableColumn<AuditEvent>[] = [
    { key: 'when', header: t('tenant.audit.when'), cell: (row) => formatDateTime(row.created_at) },
    { key: 'who', header: t('tenant.audit.who'), cell: who },
    { key: 'action', header: t('tenant.audit.action'), cell: (row) => action(row.action) },
    { key: 'reason', header: t('tenant.audit.reason'), cell: (row) => row.reason ?? t('tenant.audit.noReason') },
  ];

  if (audit.isError) {
    return (
      <div className="ui-error-state" role="alert">
        <p className="ui-error-state__text">{t('tenant.audit.loadError')}</p>
        <Button variant="secondary" onClick={() => void audit.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  }
  return (
    <>
      <Table
        ariaLabel={t('tenant.audit.listLabel')}
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        loading={audit.isPending}
        emptyState={<EmptyState title={t('tenant.audit.empty')} />}
        labels={{ empty: t('tenant.audit.empty') }}
        cardBreakpoint={CARD_BREAKPOINT}
        viewToggle={false}
        mobileViewToggle={false}
        renderCard={(row) => (
          <div className="ui-console-card">
            <strong>{action(row.action)}</strong>
            <span className="ui-console-muted">
              {formatDateTime(row.created_at)} · {who(row)}
            </span>
            {row.reason ? <span>{row.reason}</span> : null}
          </div>
        )}
      />
      {rows.length > 0 ? (
        <div className="ui-console-more">
          <span className="ui-console-muted" aria-live="polite">
            {t('tenant.audit.count', { count: rows.length })}
          </span>
          {audit.hasNextPage ? (
            <Button variant="secondary" loading={audit.isFetchingNextPage} onClick={() => void audit.fetchNextPage()}>
              {t('tenant.audit.more')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
